/**
 * Lit attribute converter for comma-separated string lists.
 * Supports HTML attributes like `exclude="special,offer"` and
 * frameworks that coerce arrays to comma-joined attribute strings.
 * @type {Object}
 * @property {function(?string): string[]} fromAttribute Converts a comma-separated attribute string into a string array.
 * @property {function(?string[]): ?string} toAttribute Converts a string array into a comma-separated attribute string, or `null` when empty.
 */
export const commaSeparatedArray = {
  fromAttribute(value) {
    if (value == null || value === '') return [];
    return value.split(',').map((s) => s.trim()).filter(Boolean);
  },
  toAttribute(value) {
    return Array.isArray(value) && value.length ? value.join(',') : null;
  }
};

/**
 * Coerce a value into a plain `Array` instance safe for `postMessage` / structured clone.
 * Unwraps framework reactive proxies (e.g. Vue `data()` arrays) and other array-likes.
 * @param {*} value Candidate array, array-like, or iterable.
 * @returns {Array} A new plain array, or `[]` when the value is not iterable.
 */
export function toPlainArray(value) {
  if (Array.isArray(value)) {
    return Array.from(value);
  }
  if (value != null && typeof value !== 'string' && typeof value[Symbol.iterator] === 'function') {
    return Array.from(value);
  }
  return [];
}

/**
 * Returns true when `value` can be structured-cloned (e.g. for iframe `postMessage`).
 * Framework reactive proxies typically fail this check.
 * @param {*} value Value to probe.
 * @returns {boolean}
 */
export function isStructuredCloneable(value) {
  try {
    structuredClone(value);
    return true;
  } catch {
    return false;
  }
}
